import { useEffect, useRef, useState, useCallback } from 'react';
import { LineStyle, LineSeries, AreaSeries } from 'lightweight-charts';
import { distanceToSegment } from '../utils/chartHelpers';

export function useDrawingTools({ chartRef, seriesRef, chartVersion }) {
  const [drawingTool, setDrawingTool] = useState(null);
  const drawingToolRef = useRef(null);
  const [isHoveringTrendline, setIsHoveringTrendline] = useState(false);
  const [selectedTL, setSelectedTL] = useState(null);
  const [showStyleOptions, setShowStyleOptions] = useState(false);
  const [measureData, setMeasureData] = useState(null);
  const [liveMeasureData, setLiveMeasureData] = useState(null);

  const drawnObjects = useRef([]);
  const trendPendingPoint = useRef(null);
  const trackingLineRef = useRef(null);
  const trackingUpdating = useRef(false);
  const objectIdCounter = useRef(0);
  const pendingRestoreRef = useRef([]);
  const hoveringTrendlineRef = useRef(false);
  const measurePendingPoint = useRef(null);

  useEffect(() => { drawingToolRef.current = drawingTool; }, [drawingTool]);

  const getNextId = useCallback(() => {
    objectIdCounter.current += 1;
    return `drawing-${objectIdCounter.current}`;
  }, []);

  const removeTrackingLine = useCallback(() => {
    if (trackingLineRef.current && chartRef.current) {
      try { chartRef.current.removeSeries(trackingLineRef.current); } catch (e) {}
      trackingLineRef.current = null;
    }
  }, [chartRef]);

  const addHorizontalLine = useCallback((price) => {
    const series = seriesRef.current;
    if (!series) return;
    const id = getNextId();
    const line = series.createPriceLine({
      price, color: '#FF9800', lineWidth: 2, lineStyle: LineStyle.Solid,
      axisLabelVisible: true, title: `── ${price.toFixed(2)}`,
    });
    drawnObjects.current.push({
      id, type: 'horizontal', price, ref: line,
      remove: () => series.removePriceLine(line),
    });
  }, [seriesRef, getNextId]);

  const addTrendLine = useCallback((point1, point2) => {
    const chart = chartRef.current;
    if (!chart) return;
    const id = getNextId();
    const lineSeries = chart.addSeries(LineSeries, {
      color: '#2962FF', lineWidth: 2, lineStyle: LineStyle.Solid,
      lastValueVisible: false, priceLineVisible: false,
    });
    const points = [
      { time: point1.time, value: point1.price },
      { time: point2.time, value: point2.price },
    ].sort((a, b) => a.time - b.time);
    lineSeries.setData(points);
    drawnObjects.current.push({
      id, type: 'trend', points: [point1, point2], ref: lineSeries,
      color: '#2962FF', lineStyle: LineStyle.Solid,
      remove: () => chart.removeSeries(lineSeries),
    });
  }, [chartRef, getNextId]);

  const addMeasure = useCallback((point1, point2) => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    const priceDiff = point2.price - point1.price;
    const percentChange = point1.price !== 0 ? (priceDiff / point1.price) * 100 : 0;
    const timeDiffSeconds = Math.abs(point2.time - point1.time);

    const id = getNextId();

    // Sort by time
    const [pA, pB] = point1.time <= point2.time
      ? [{ time: point1.time, value: point1.price }, { time: point2.time, value: point2.price }]
      : [{ time: point2.time, value: point2.price }, { time: point1.time, value: point1.price }];

    // Connection line
    const connSeries = chart.addSeries(LineSeries, {
      color: '#FF9800', lineWidth: 1.5, lineStyle: LineStyle.Solid,
      lastValueVisible: false, priceLineVisible: false,
    });
    connSeries.setData([pA, pB]);

    // Filled area under the connection line
    const areaSeries = chart.addSeries(AreaSeries, {
      topColor: 'rgba(255, 152, 0, 0.25)',
      bottomColor: 'rgba(255, 152, 0, 0.02)',
      lineColor: 'transparent',
      lineWidth: 0,
      lastValueVisible: false,
      priceLineVisible: false,
    });
    areaSeries.setData([pA, pB]);

    // Calculate popup position - midpoint
    const x1 = chart.timeScale().timeToCoordinate(point1.time);
    const y1 = series.priceToCoordinate(point1.price);
    const x2 = chart.timeScale().timeToCoordinate(point2.time);
    const y2 = series.priceToCoordinate(point2.price);

    drawnObjects.current.push({
      id, type: 'measure', points: [point1, point2], ref: connSeries,
      areaRef: areaSeries,
      remove: () => {
        try { chart.removeSeries(connSeries); } catch (e) {}
        try { chart.removeSeries(areaSeries); } catch (e) {}
      },
    });
    setMeasureData({
      pointA: point1, pointB: point2,
      priceDiff, percentChange, timeDiffSeconds,
      midX: x1 != null && x2 != null ? (x1 + x2) / 2 : (x1 || x2 || 0),
      midY: y1 != null && y2 != null ? (y1 + y2) / 2 : (y1 || y2 || 0),
    });
  }, [chartRef, seriesRef, getNextId]);

  const computeMeasureData = useCallback((pointA, pointB) => {
    if (!pointA || !pointB) return null;
    const priceDiff = pointB.price - pointA.price;
    const percentChange = pointA.price !== 0 ? (priceDiff / pointA.price) * 100 : 0;
    const timeDiffSeconds = Math.abs(pointB.time - pointA.time);
    return { pointA, pointB, priceDiff, percentChange, timeDiffSeconds };
  }, []);

  const clearAllDrawings = useCallback(() => {
    drawnObjects.current.forEach(o => o.remove());
    drawnObjects.current = [];
    pendingRestoreRef.current = [];
    trendPendingPoint.current = null;
    measurePendingPoint.current = null;
    removeTrackingLine();
    setShowStyleOptions(false);
    setSelectedTL(null);
    setMeasureData(null);
    setLiveMeasureData(null);
  }, [removeTrackingLine]);

  const handleToolSelect = useCallback((tool) => {
    setDrawingTool(prev => prev === tool ? null : tool);
    trendPendingPoint.current = null;
    measurePendingPoint.current = null;
    removeTrackingLine();
    setShowStyleOptions(false);
    setSelectedTL(null);
    setMeasureData(null);
    setLiveMeasureData(null);
  }, [removeTrackingLine]);

  const updateTLColor = (color) => {
    const obj = drawnObjects.current.find(o => o.id === selectedTL?.id);
    if (!obj?.ref) return;
    obj.ref.applyOptions({ color });
    obj.color = color;
    setSelectedTL(s => s ? { ...s, color } : s);
  };

  const updateTLLineStyle = (style) => {
    const obj = drawnObjects.current.find(o => o.id === selectedTL?.id);
    if (!obj?.ref) return;
    obj.ref.applyOptions({ lineStyle: style });
    obj.lineStyle = style;
    setSelectedTL(s => s ? { ...s, lineStyle: style } : s);
  };

  const removeTL = () => {
    const obj = drawnObjects.current.find(o => o.id === selectedTL?.id);
    if (!obj) return;
    obj.remove();
    drawnObjects.current = drawnObjects.current.filter(o => o.id !== selectedTL?.id);
    setSelectedTL(null);
  };

  // Subscribe chart event handlers whenever chart is recreated
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    // Restore persisted trendlines
    const toRestore = pendingRestoreRef.current;
    pendingRestoreRef.current = [];
    toRestore.forEach(t => {
      const lineSeries = chart.addSeries(LineSeries, {
        color: t.color || '#2962FF',
        lineWidth: 2,
        lineStyle: t.lineStyle ?? LineStyle.Solid,
        lastValueVisible: false,
        priceLineVisible: false,
      });
      const pts = [
        { time: t.points[0].time, value: t.points[0].price },
        { time: t.points[1].time, value: t.points[1].price },
      ].sort((a, b) => a.time - b.time);
      lineSeries.setData(pts);
      drawnObjects.current.push({
        id: t.id || `restored-${Date.now()}-${Math.random()}`,
        type: 'trend', points: t.points, ref: lineSeries,
        color: t.color || '#2962FF',
        lineStyle: t.lineStyle ?? LineStyle.Solid,
        remove: () => chart.removeSeries(lineSeries),
      });
    });

    const handleClick = (param) => {
      const tool = drawingToolRef.current;
      if (!param.point) return;
      const px = param.point.x, py = param.point.y;
      const time = chart.timeScale().coordinateToTime(px);
      const price = series.coordinateToPrice(py);
      if (time === null || price === null) return;

      if (tool === 'horizontal') {
        addHorizontalLine(price);
        setDrawingTool(null);
      } else if (tool === 'measure') {
        if (!measurePendingPoint.current) {
          measurePendingPoint.current = { time, price };
        } else {
          removeTrackingLine();
          addMeasure(measurePendingPoint.current, { time, price });
          measurePendingPoint.current = null;
          setLiveMeasureData(null);
          setDrawingTool(null);
        }
      } else if (tool === 'trend') {
        if (!trendPendingPoint.current) {
          trendPendingPoint.current = { time, price };
        } else {
          removeTrackingLine();
          addTrendLine(trendPendingPoint.current, { time, price });
          trendPendingPoint.current = null;
          setDrawingTool(null);
        }
      } else {
        for (const obj of drawnObjects.current) {
          if (obj.type !== 'trend') continue;
          const x1 = chart.timeScale().timeToCoordinate(obj.points[0].time);
          const y1 = series.priceToCoordinate(obj.points[0].price);
          const x2 = chart.timeScale().timeToCoordinate(obj.points[1].time);
          const y2 = series.priceToCoordinate(obj.points[1].price);
          if (x1 == null || y1 == null || x2 == null || y2 == null) continue;
          if (distanceToSegment(px, py, x1, y1, x2, y2) < 10) {
            setShowStyleOptions(false);
            setSelectedTL({ id: obj.id, color: obj.color, lineStyle: obj.lineStyle, x: px, y: py });
            return;
          }
        }
        setShowStyleOptions(false);
        setSelectedTL(null);
      }
    };

    const handleCrosshair = (param) => {
      const tool = drawingToolRef.current;

      let nearTrendline = false;
      if (!tool && param.point && param.point.x != null) {
        const px = param.point.x, py = param.point.y;
        for (const obj of drawnObjects.current) {
          if (obj.type !== 'trend') continue;
          const x1 = chart.timeScale().timeToCoordinate(obj.points[0].time);
          const y1 = series.priceToCoordinate(obj.points[0].price);
          const x2 = chart.timeScale().timeToCoordinate(obj.points[1].time);
          const y2 = series.priceToCoordinate(obj.points[1].price);
          if (x1 == null || y1 == null || x2 == null || y2 == null) continue;
          if (distanceToSegment(px, py, x1, y1, x2, y2) < 10) { nearTrendline = true; break; }
        }
      }
      if (nearTrendline !== hoveringTrendlineRef.current) {
        hoveringTrendlineRef.current = nearTrendline;
        setIsHoveringTrendline(nearTrendline);
      }

      // Live measure data during tracking
      if (tool === 'measure' && measurePendingPoint.current && param.point) {
        const t = chart.timeScale().coordinateToTime(param.point.x);
        const p = series.coordinateToPrice(param.point.y);
        if (t != null && p != null) {
          const live = computeMeasureData(measurePendingPoint.current, { time: t, price: p });
          if (live) setLiveMeasureData(live);
        }
      } else {
        setLiveMeasureData(null);
      }

      // Tracking line for trend / measure
      if ((tool === 'trend' && trendPendingPoint.current) || (tool === 'measure' && measurePendingPoint.current)) {
        const pending = tool === 'trend' ? trendPendingPoint.current : measurePendingPoint.current;
        if (!param.point || trackingUpdating.current) return;
        trackingUpdating.current = true;
        try {
          const t = chart.timeScale().coordinateToTime(param.point.x);
          const p = series.coordinateToPrice(param.point.y);
          if (t == null || p == null) return;
          if (t === pending.time) return;
          const pts = [
            { time: pending.time, value: pending.price },
            { time: t, value: p },
          ].sort((a, b) => a.time - b.time);
          if (!trackingLineRef.current) {
            trackingLineRef.current = chart.addSeries(LineSeries, {
              color: tool === 'measure' ? 'rgba(255, 152, 0, 0.7)' : 'rgba(245, 242, 242, 0.95)',
              lineWidth: 1,
              lineStyle: LineStyle.Dashed, lastValueVisible: false, priceLineVisible: false,
            });
          }
          trackingLineRef.current.setData(pts);
        } finally { trackingUpdating.current = false; }
      } else {
        if (trackingLineRef.current) {
          try { chart.removeSeries(trackingLineRef.current); } catch (e) {}
          trackingLineRef.current = null;
        }
      }
    };

    chart.subscribeClick(handleClick);
    chart.subscribeCrosshairMove(handleCrosshair);

    return () => {
      chart.unsubscribeClick(handleClick);
      chart.unsubscribeCrosshairMove(handleCrosshair);
      if (trackingLineRef.current) {
        try { chart.removeSeries(trackingLineRef.current); } catch (e) {}
        trackingLineRef.current = null;
      }
      pendingRestoreRef.current = drawnObjects.current
        .filter(o => o.type === 'trend')
        .map(o => ({ points: o.points, color: o.color, lineStyle: o.lineStyle }));
      drawnObjects.current.forEach(o => { try { o.remove(); } catch (e) {} });
      drawnObjects.current = [];
      trendPendingPoint.current = null;
      measurePendingPoint.current = null;
    };
  }, [chartVersion, chartRef, seriesRef, addHorizontalLine, addTrendLine, addMeasure, computeMeasureData, removeTrackingLine]);

  return {
    drawingTool, setDrawingTool, isHoveringTrendline,
    selectedTL, setSelectedTL, showStyleOptions, setShowStyleOptions,
    clearAllDrawings, handleToolSelect,
    updateTLColor, updateTLLineStyle, removeTL,
    measureData, setMeasureData, liveMeasureData,
  };
}
